import { Stack, Typography } from "@mui/material";
import { useRef, useEffect } from "react";
import { useGetTextMessagesQuery } from "src/api/services/smsApi";
import { PatientProfile } from "src/api/types";
import TextMessage from "../TextMessage/TextMessage";

type DataError = {
  data: {
    error: string;
    message: string;
    statusCode: number;
  };
};

const IsFetchBaseQueryError = (err: unknown): err is DataError => {
  return (
    err != null &&
    typeof err === "object" &&
    "data" in err &&
    err.data != null &&
    typeof err.data === "object" &&
    "message" in err.data
  );
};

interface Props {
  patient: PatientProfile;
}

const TextMessagesList = ({ patient }: Props) => {
  const {
    data: messages = [],
    error,
    isError,
    isLoading,
  } = useGetTextMessagesQuery({
    patient_profile_id: patient.profile_id,
  });

  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (isError === true) {
    let message = "Error fetching text messages";
    if (IsFetchBaseQueryError(error)) {
      message = error.data.message;
    }

    if ("message" in error && error.message != null) {
      message = error.message;
    }

    return <Typography color={"error"}>{message}</Typography>;
  }

  if (isLoading === false && messages.length === 0) {
    return <Typography>No data available</Typography>;
  }

  return (
    <Stack
      sx={{
        overflowY: "scroll",
        height: "500px",
        mt: 2,
        mb: 2,
        pl: 0.5,
        pr: 0.5,
      }}
      role="list"
      aria-label="Text messages"
    >
      {messages.map((message) => (
        <TextMessage textMessage={message} key={message.id} />
      ))}
      <div ref={bottomRef} />
    </Stack>
  );
};

export default TextMessagesList;
